import { memo, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type FocusEvent, type KeyboardEvent, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { descriptionHtml, descriptionPreview } from '@/lib/descriptionHtml';
import { cn } from '@/lib/utils';

/**
 * A ticket's description in a grid cell: one truncated line. The full text, formatted (lib/descriptionHtml), shows in ONE tooltip per table
 * (`useDescriptionTooltip`, delegated on the `<tbody>`), not a Radix tooltip per row.
 * `description` is `undefined` while the grid's descriptions load (useTicketDescriptions), `null` for none.
 * The descriptions endpoint caps them (~1,500 chars); the ticket page has the full text.
 */
export const TicketDescriptionCell = memo(function TicketDescriptionCell({
  ticketId, description,
}: { ticketId: number; description: string | null | undefined }) {
  // The cell shows one cleaned-up line (no cid: placeholders or <mailto:> noise); the tooltip shows it formatted.
  const oneLine = useMemo(() => descriptionPreview(description ?? ''), [description]);
  if (description === undefined) return <span className="text-xs text-muted-foreground/40">…</span>;
  if (!oneLine) return <span className="text-muted-foreground/60">—</span>;
  return (
    <div
      data-desc-ticket={ticketId}
      tabIndex={0}
      className="cursor-default truncate rounded-sm text-xs text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      {oneLine}
    </div>
  );
});

const OPEN_DELAY = 300;   // Radix's usual tooltip delay
const SKIP_DELAY = 300;   // moving to the next cell soon after one closed opens at once (like Radix skipDelayDuration)
const CLOSE_DELAY = 120;  // grace to move the pointer into the tooltip (it scrolls)
const GAP = 6;
const INSET = 1 / 3;      // the tooltip starts this far across the description cell
const PAD = 8;

const CELL = '[data-desc-ticket]';

/**
 * One description tooltip for a whole table. Spread `bind` on the `<tbody>` and render `tooltip`.
 * Opens on hover (after a delay) or keyboard focus of a `TicketDescriptionCell`, stays open while the pointer
 * is over the tooltip (so long text can be scrolled), closes on leave / blur / Escape / page scroll.
 * Portalled with z-[60] like TooltipContent, so it sits above dialogs and escapes overflow-hidden tables.
 */
export function useDescriptionTooltip(descriptions: Map<number, string | null>) {
  const tipId = useId();
  const [open, setOpen] = useState<{ id: number; el: HTMLElement } | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const tipRef = useRef<HTMLDivElement | null>(null);
  const showTimer = useRef<number | undefined>(undefined);
  const hideTimer = useRef<number | undefined>(undefined);
  const pendingEl = useRef<HTMLElement | null>(null);
  const lastClosedAt = useRef(0);

  const api = useMemo(() => {
    const close = () => {
      window.clearTimeout(showTimer.current);
      window.clearTimeout(hideTimer.current);
      pendingEl.current = null;
      const cur = openRef.current;
      if (!cur) return;
      cur.el.removeAttribute('aria-describedby');
      lastClosedAt.current = Date.now();
      openRef.current = null;
      setOpen(null);
    };
    const show = (el: HTMLElement, immediate: boolean) => {
      window.clearTimeout(hideTimer.current);
      const cur = openRef.current;
      if (cur?.el === el) return;
      if (pendingEl.current === el && !immediate) return;
      window.clearTimeout(showTimer.current);
      const id = Number(el.dataset.descTicket);
      if (!Number.isFinite(id)) return;
      const doShow = () => {
        pendingEl.current = null;
        openRef.current?.el.removeAttribute('aria-describedby');
        el.setAttribute('aria-describedby', tipId);
        const next = { id, el };
        openRef.current = next;
        setOpen(next);
      };
      if (immediate || cur || Date.now() - lastClosedAt.current < SKIP_DELAY) { doShow(); return; }
      pendingEl.current = el;
      showTimer.current = window.setTimeout(doShow, OPEN_DELAY);
    };
    const hideSoon = () => {
      window.clearTimeout(showTimer.current);
      pendingEl.current = null;
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(close, CLOSE_DELAY);
    };
    const cellOf = (t: EventTarget | null) => (t instanceof Element ? (t.closest(CELL) as HTMLElement | null) : null);

    return {
      close,
      hideSoon,
      cancelHide: () => window.clearTimeout(hideTimer.current),
      bind: {
        onPointerOver: (e: PointerEvent<HTMLElement>) => {
          if (e.pointerType === 'touch') return;
          const el = cellOf(e.target);
          if (el && e.currentTarget.contains(el)) show(el, false);
        },
        onPointerOut: (e: PointerEvent<HTMLElement>) => {
          if (e.pointerType === 'touch') return;
          const el = cellOf(e.target);
          if (!el) return;
          if (e.relatedTarget instanceof Node && el.contains(e.relatedTarget)) return;
          if (pendingEl.current === el) { window.clearTimeout(showTimer.current); pendingEl.current = null; }
          if (openRef.current?.el === el) hideSoon();
        },
        onFocus: (e: FocusEvent<HTMLElement>) => {
          const el = cellOf(e.target);
          if (el && el === e.target) show(el, true);
        },
        onBlur: (e: FocusEvent<HTMLElement>) => {
          if (openRef.current?.el === e.target) close();
        },
        onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Escape' && openRef.current) close();
        },
      },
    };
  }, [tipId]);

  // Clear timers on unmount.
  useEffect(() => () => { window.clearTimeout(showTimer.current); window.clearTimeout(hideTimer.current); }, []);

  // While open: close on page scroll (not the tooltip's own) or resize, or if the row went away.
  useEffect(() => {
    if (!open) return;
    if (!open.el.isConnected) { api.close(); return; }
    const onScroll = (e: Event) => {
      if (tipRef.current && e.target instanceof Node && tipRef.current.contains(e.target)) return;
      api.close();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', api.close);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', api.close);
    };
  }, [open, api, descriptions]);

  // Place it below the cell (above when there's no room), starting a third of the way across it, so the start of
  // the description column stays readable for the rows it overlaps. Kept inside the viewport. Before paint.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!open || !tip) return;
    const r = open.el.getBoundingClientRect();
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const left = Math.max(PAD, Math.min(r.left + r.width * INSET, window.innerWidth - w - PAD));
    let top = r.bottom + GAP;
    if (top + h > window.innerHeight - PAD && r.top - GAP - h >= PAD) top = r.top - GAP - h;
    tip.style.left = `${left}px`;
    tip.style.top = `${top}px`;
    tip.style.visibility = 'visible';
  }, [open]);

  const text = open ? (descriptions.get(open.id) ?? '').trim() : '';
  // Formatted from the plain-text description: every piece of text is escaped and only descriptionHtml()'s own
  // tags are emitted (see lib/descriptionHtml.ts), so this is safe to set as HTML.
  const html = useMemo(() => (text ? descriptionHtml(text) : ''), [text]);
  const tooltip = open && html
    ? createPortal(
        <div
          ref={tipRef}
          id={tipId}
          role="tooltip"
          onPointerEnter={api.cancelHide}
          onPointerLeave={api.hideSoon}
          style={{ position: 'fixed', top: 0, left: 0, visibility: 'hidden' }}
          className={cn(
            'z-[60] rounded-md border border-border bg-popover px-3 py-2 text-popover-foreground shadow-lg animate-in fade-in-0',
            'max-h-96 max-w-lg overflow-y-auto break-words text-[12.5px] leading-relaxed',
            '[&_p]:my-1.5 [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-0.5',
            '[&_hr]:my-2 [&_hr]:border-border [&_a]:text-brand-strong [&_a]:underline [&_a]:underline-offset-2',
          )}
          dangerouslySetInnerHTML={{ __html: html }}
        />,
        document.body,
      )
    : null;

  return { bind: api.bind, tooltip };
}
